import { CollectionEvents } from '../utils/events';
import { clearSectionCache, getSectionHTML } from '../utils/section-renderer';

export default class ProductsGrid extends HTMLElement {
  private pages = new Map<number, string>();
  private isLoading = false;

  private onClickBound = this.onClick.bind(this);
  private onFilterUpdateBound = this.onFilterUpdate.bind(this);

  connectedCallback(): void {
    this.addEventListener('click', this.onClickBound);
    document.addEventListener(CollectionEvents.FilterUpdate, this.onFilterUpdateBound);
    void this.prefetchPage(this.getNextPage());
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.onClickBound);
    document.removeEventListener(CollectionEvents.FilterUpdate, this.onFilterUpdateBound);
  }

  private get sectionId(): string {
    const id = this.getAttribute('section-id');

    if (!id) {
      throw new Error('The section-id attribute is required on <products-grid>');
    }

    return id;
  }

  private onClick(event: Event): void {
    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>(
      '[data-products-grid-ref="load-more-button"]',
    );

    if (!link) {
      return;
    }

    event.preventDefault();

    if (this.isLoading) {
      return;
    }

    void this.renderNextPage();
  }

  private onFilterUpdate(): void {
    this.pages.clear();
    clearSectionCache();
    void this.prefetchPage(this.getNextPage());
  }

  private getCurrentPage(): number {
    const items = this.querySelectorAll<HTMLElement>('[data-products-grid-ref="item"]');
    const lastItem = items[items.length - 1];

    return Number(lastItem?.dataset.page ?? 1);
  }

  private getLastPage(): number {
    const grid = this.querySelector<HTMLElement>('.main-grid');

    return Number(grid?.dataset.lastPage ?? 1);
  }

  private getNextPage(): number | null {
    const currentPage = this.getCurrentPage();
    const lastPage = this.getLastPage();

    if (currentPage >= lastPage) {
      return null;
    }

    return currentPage + 1;
  }

  private buildPageUrl(page: number): URL {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';
    return url;
  }

  private async prefetchPage(page: number | null): Promise<void> {
    if (!page || this.pages.has(page) || page < 1 || page > this.getLastPage()) {
      return;
    }

    try {
      const sectionHtml = await getSectionHTML(this.sectionId, this.buildPageUrl(page));
      this.pages.set(page, sectionHtml);
    } catch (error) {
      console.error(error);
    }
  }

  private getGridItemsForPage(page: number): Node[] | null {
    const pageHtml = this.pages.get(page);

    if (!pageHtml) {
      return null;
    }

    const parsedPage = new DOMParser().parseFromString(pageHtml, 'text/html');
    const grid = this.getMainGridFromRoot(parsedPage);

    if (!grid) {
      return null;
    }

    return [...grid.querySelectorAll(':scope > [data-products-grid-ref="item"]')];
  }

  private getLoadMoreWrapperFromPage(pageHtml: string): HTMLElement | null {
    const parsedPage = new DOMParser().parseFromString(pageHtml, 'text/html');

    return parsedPage.querySelector('[data-products-grid-ref="load-more-wrapper"]');
  }

  private getProductsContainerFromRoot(root: ParentNode): HTMLElement | null {
    return root.querySelector<HTMLElement>('[id$="-products-grid"]');
  }

  private getMainGridFromRoot(root: ParentNode): HTMLElement | null {
    return this.getProductsContainerFromRoot(root)?.querySelector<HTMLElement>('.main-grid') ?? null;
  }

  private async renderNextPage(): Promise<void> {
    const nextPage = this.getNextPage();
    const container = this.getProductsContainerFromRoot(this);
    const currentGrid = this.getMainGridFromRoot(this);

    if (!nextPage || !container || !currentGrid) {
      return;
    }

    this.isLoading = true;

    try {
      if (!this.pages.has(nextPage)) {
        const sectionHtml = await getSectionHTML(this.sectionId, this.buildPageUrl(nextPage));
        this.pages.set(nextPage, sectionHtml);
      }

      const nextPageItems = this.getGridItemsForPage(nextPage);

      if (!nextPageItems?.length) {
        throw new Error(`No products found for page ${nextPage}`);
      }

      currentGrid.append(...nextPageItems);

      const pageHtml = this.pages.get(nextPage);
      const currentLoadMoreWrapper = this.querySelector('[data-products-grid-ref="load-more-wrapper"]');

      if (currentLoadMoreWrapper) {
        const newLoadMoreWrapper = pageHtml ? this.getLoadMoreWrapperFromPage(pageHtml) : null;

        if (newLoadMoreWrapper) {
          currentLoadMoreWrapper.replaceWith(newLoadMoreWrapper);
        } else {
          currentLoadMoreWrapper.remove();
        }
      }

      // const nextPageUrl = this.buildPageUrl(nextPage);
      // history.pushState({}, '', `${nextPageUrl.pathname}${nextPageUrl.search}`);

      void this.prefetchPage(this.getNextPage());
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }
}

customElements.define('products-grid', ProductsGrid);
